import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SellerLayout } from '../components/SellerLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { colors } from '../constants/colors';
import { getOrdersBySeller, downloadBulkPickLists } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { parseUTC } from '../utils/date';
import type { Order, OrderStatus } from '../types';
import { Package, Clock, Truck, Check, ExternalLink, Download, Loader2, X, AlertCircle } from 'lucide-react';
import { PaginationControls } from '../components/PaginationControls';
import type { LucideIcon } from 'lucide-react';

const PAGE_SIZE = 20;

type OrderFilterValue = OrderStatus | 'ALL' | 'PAYMENT_FAILED';

const STATUS_OPTIONS: { value: OrderFilterValue; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'UNSHIPPED', label: 'Unshipped' },
  { value: 'PARTIALLY_SHIPPED', label: 'Partially Shipped' },
  { value: 'LABEL_CREATED', label: 'Label Created' },
  { value: 'SHIPPED', label: 'Shipped' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'PAYMENT_FAILED', label: 'Payment Failed' },
];

const CARRIER_LOGOS: Record<string, string> = {
  USPS: '/img/logos/carrier-usps.svg',
  UPS: '/img/logos/carrier-ups.svg',
  FedEx: '/img/logos/carrier-fx.svg',
};

function getTrackingUrl(carrier: string, trackingNumber: string): string | null {
  switch (carrier) {
    case 'USPS': return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    case 'UPS': return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    case 'FedEx': return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    default: return null;
  }
}

const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bgColor: string; Icon: LucideIcon }> = {
  OPEN: { label: 'Open', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  UNSHIPPED: { label: 'Unshipped', color: '#F59E0B', bgColor: '#FEF3C7', Icon: Clock },
  PARTIALLY_SHIPPED: { label: 'Partially Shipped', color: '#F97316', bgColor: '#FFF7ED', Icon: Package },
  LABEL_CREATED: { label: 'Label Created', color: '#5856D6', bgColor: '#EDE9FE', Icon: Package },
  SHIPPED: { label: 'Shipped', color: '#007AFF', bgColor: '#DBEAFE', Icon: Truck },
  DELIVERED: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7', Icon: Check },
  CANCELLED: { label: 'Cancelled', color: colors.gray[600], bgColor: colors.gray[100], Icon: X },
};

function formatDate(dateString: string): string {
  const date = parseUTC(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ', ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function SellerOrdersPage() {
  useDocumentTitle('Orders');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [unshippedCount, setUnshippedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);
  const [isDownloadingBulk, setIsDownloadingBulk] = useState(false);

  // Get filter and page from URL
  const statusFilter = (searchParams.get('status') as OrderFilterValue) || 'ALL';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Clear selection on filter/page change
  useEffect(() => {
    setSelectedIds(new Set());
    setAllMatchingSelected(false);
  }, [statusFilter, currentPage]);

  // Fetch orders when page or filter changes
  useEffect(() => {
    if (user?.id) {
      loadOrders(currentPage, statusFilter);
    }
  }, [user?.id, currentPage, statusFilter]);

  // Fetch unshipped count on mount
  useEffect(() => {
    if (user?.id) {
      getOrdersBySeller(0, 1, 'UNSHIPPED').then(data => {
        setUnshippedCount(data.totalElements);
      }).catch(() => {});
    }
  }, [user?.id]);

  const loadOrders = async (page: number, status: OrderFilterValue) => {
    setIsLoading(true);
    try {
      const isPaymentFailed = status === 'PAYMENT_FAILED';
      const statusParam = (status === 'ALL' || isPaymentFailed) ? undefined : status;
      const data = await getOrdersBySeller(page - 1, PAGE_SIZE, statusParam, isPaymentFailed || undefined);
      setOrders(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const setStatusFilter = (status: OrderFilterValue) => {
    const params = new URLSearchParams(searchParams);
    if (status === 'ALL') {
      params.delete('status');
    } else {
      params.set('status', status);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const setPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  // Selection helpers
  const allOnPageSelected = orders.length > 0 && orders.every(s => selectedIds.has(s.id));
  const someOnPageSelected = orders.some(s => selectedIds.has(s.id));
  const hasSelection = selectedIds.size > 0 || allMatchingSelected;
  const hasMorePages = totalPages > 1;
  const statusLabel = STATUS_OPTIONS.find(o => o.value === statusFilter)?.label || 'All';

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds(new Set());
      setAllMatchingSelected(false);
    } else {
      setSelectedIds(new Set(orders.map(s => s.id)));
      setAllMatchingSelected(false);
    }
  };

  const toggleSelectOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
      setAllMatchingSelected(false);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setAllMatchingSelected(false);
  };

  const handleBulkDownload = async () => {
    try {
      setIsDownloadingBulk(true);
      const request: { orderIds?: string[]; status?: string } = {};
      if (allMatchingSelected) {
        request.status = (statusFilter === 'ALL' || statusFilter === 'PAYMENT_FAILED') ? undefined : statusFilter;
      } else {
        request.orderIds = [...selectedIds];
      }
      const blob = await downloadBulkPickLists(request);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) {
      console.error('Failed to download pick lists:', err);
    } finally {
      setIsDownloadingBulk(false);
    }
  };

  const selectionCount = allMatchingSelected ? totalElements : selectedIds.size;

  const GRID_COLUMNS = '40px 0.8fr 1.5fr 1fr 0.8fr 1fr 1.2fr 1.5fr';

  const renderOrderRow = (order: Order) => {
    const statusConfig = STATUS_CONFIG[order.status];
    const isSelected = selectedIds.has(order.id) || allMatchingSelected;
    return (
      <div
        key={order.id}
        style={{
          ...styles.tableRow,
          gridTemplateColumns: GRID_COLUMNS,
          ...(isSelected ? { backgroundColor: '#EFF6FF' } : {}),
        }}
        onClick={() => navigate(`/sell/orders/${order.id}`)}
      >
        <span
          style={styles.checkboxCell}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelectOne(order.id)}
            style={styles.checkbox}
          />
        </span>
        <span style={{ ...styles.tableCell, fontFamily: 'monospace', fontSize: '13px', fontWeight: 600 }}>{order.id}</span>
        <span style={styles.tableCell}>{order.recipientName}</span>
        <span style={styles.tableCell}>{formatDate(order.createdAt)}</span>
        <span style={styles.tableCell}>{order.itemSaleCount} item{order.itemSaleCount !== 1 ? 's' : ''}</span>
        <span style={styles.tableCell}>{formatCurrency(order.totalValue)}</span>
        <span style={styles.tableCell}>
          {order.hasFailedPayments ? (
            <span style={{
              ...styles.statusBadge,
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
            }}>
              <AlertCircle size={14} />
              Payment Failed
            </span>
          ) : (
            <span style={{
              ...styles.statusBadge,
              backgroundColor: statusConfig.bgColor,
              color: statusConfig.color,
            }}>
              <statusConfig.Icon size={14} />
              {statusConfig.label}
            </span>
          )}
        </span>
        <span style={styles.tableCell}>
          {(() => {
            const shipments = (order.shipments ?? []).filter(s => s.trackingNumber);
            if (shipments.length === 0) {
              return <span style={styles.noTracking}>-</span>;
            }
            return (
              <span style={styles.trackingColumn}>
                {shipments.map((s, i) => {
                  const carrier = s.carrier ?? '';
                  const url = getTrackingUrl(carrier, s.trackingNumber!);
                  return (
                    <span key={s.id} style={i > 0 ? { marginTop: 6 } : undefined}>
                      <span style={styles.trackingCarrierRow}>
                        {CARRIER_LOGOS[carrier] && (
                          <img src={CARRIER_LOGOS[carrier]} alt={carrier} style={styles.carrierLogoInline} />
                        )}
                        <span>{carrier}</span>
                      </span>
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.trackingLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {s.trackingNumber}
                          <ExternalLink size={12} />
                        </a>
                      ) : (
                        <span style={styles.trackingNumberText}>{s.trackingNumber}</span>
                      )}
                    </span>
                  );
                })}
              </span>
            );
          })()}
        </span>
      </div>
    );
  };

  return (
    <SellerLayout>
      {isLoading ? (
        <div style={styles.loadingState}>Loading orders...</div>
      ) : totalElements === 0 && statusFilter === 'ALL' ? (
        <div style={styles.emptyState}>
          <Package size={48} color={colors.gray[300]} />
          <p style={{ marginTop: '12px' }}>No orders yet</p>
          <p style={{ fontSize: '14px', color: colors.gray[600] }}>
            Orders will appear here after shows are settled
          </p>
        </div>
      ) : (
        <>
          {/* Unshipped Alert Banner */}
          {unshippedCount > 0 && statusFilter !== 'UNSHIPPED' && (
            <section style={styles.alertBanner}>
              <div style={styles.alertContent}>
                <Clock size={18} color="#F59E0B" />
                <span style={styles.alertText}>
                  {unshippedCount} order{unshippedCount > 1 ? 's need' : ' needs'} to be shipped
                </span>
              </div>
              <button
                style={styles.alertButton}
                onClick={() => setStatusFilter('UNSHIPPED')}
              >
                View Unshipped
              </button>
            </section>
          )}

          {/* Orders Header */}
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Orders</h2>
            <span style={styles.orderCount}>
              {totalElements} {totalElements === 1 ? 'order' : 'orders'}
            </span>
          </div>

          {/* Status Filters */}
          <div style={styles.filterRow}>
            {STATUS_OPTIONS.map((option) => {
              const isActive = statusFilter === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => setStatusFilter(option.value)}
                  style={{
                    ...styles.filterButton,
                    ...(isActive ? styles.filterButtonActive : {}),
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {/* Action Bar — visible when selection exists */}
          {hasSelection && (
            <div style={styles.actionBar}>
              <div style={styles.actionBarLeft}>
                <span style={styles.selectionText}>
                  {allMatchingSelected
                    ? `All ${totalElements} orders matching '${statusLabel}' selected`
                    : `${selectionCount} selected`}
                </span>
                <button style={styles.clearButton} onClick={clearSelection}>
                  Clear
                </button>
              </div>
              <button
                style={styles.downloadButton}
                onClick={handleBulkDownload}
                disabled={isDownloadingBulk}
              >
                {isDownloadingBulk ? (
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Download size={16} />
                )}
                {isDownloadingBulk ? 'Downloading...' : 'Download Pick Lists'}
              </button>
            </div>
          )}

          {/* Orders Table */}
          {orders.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={{ color: colors.gray[600] }}>No orders match this filter</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: isMobile ? 'auto' : undefined, WebkitOverflowScrolling: 'touch' }}>
              <div style={{ ...styles.ordersTable, minWidth: isMobile ? '800px' : undefined }}>
                <div style={{ ...styles.tableHeader, gridTemplateColumns: GRID_COLUMNS }}>
                  <span style={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
                      }}
                      onChange={toggleSelectAll}
                      style={styles.checkbox}
                    />
                  </span>
                  <span style={styles.tableHeaderCell}>Order Number</span>
                  <span style={styles.tableHeaderCell}>Recipient</span>
                  <span style={styles.tableHeaderCell}>Order Date</span>
                  <span style={styles.tableHeaderCell}>Items</span>
                  <span style={styles.tableHeaderCell}>Value</span>
                  <span style={styles.tableHeaderCell}>Status</span>
                  <span style={styles.tableHeaderCell}>Tracking</span>
                </div>

                {/* Gmail-style "select all matching" banner */}
                {allOnPageSelected && hasMorePages && !allMatchingSelected && (
                  <div style={styles.selectAllBanner}>
                    All {orders.length} orders on this page are selected.{' '}
                    <button
                      style={styles.selectAllLink}
                      onClick={() => setAllMatchingSelected(true)}
                    >
                      Select all {totalElements} orders matching &lsquo;{statusLabel}&rsquo;
                    </button>
                  </div>
                )}

                {orders.map((order) => renderOrderRow(order))}
              </div>
              </div>

              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </>
      )}
    </SellerLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  alertBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FEF3C7',
    borderRadius: '12px',
    padding: '12px 16px',
    border: '1px solid #F59E0B',
    marginBottom: '16px',
  },
  alertContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  alertText: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#92400E',
  },
  alertButton: {
    padding: '6px 12px',
    backgroundColor: '#F59E0B',
    color: colors.white,
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 700,
    margin: 0,
    color: colors.ink.black,
  },
  orderCount: {
    fontSize: '14px',
    color: colors.gray[600],
  },
  filterRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: colors.gray[100],
    color: colors.gray[700],
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  filterButtonActive: {
    backgroundColor: colors.ink.black,
    color: colors.white,
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    backgroundColor: '#EFF6FF',
    borderRadius: '8px',
    marginBottom: '12px',
    border: '1px solid #BFDBFE',
  },
  actionBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectionText: {
    fontSize: '13px',
    fontWeight: 500,
    color: colors.ink.black,
  },
  clearButton: {
    background: 'none',
    border: 'none',
    color: colors.brand.blue,
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
  },
  downloadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    backgroundColor: colors.ink.black,
    color: colors.white,
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  selectAllBanner: {
    padding: '8px 16px',
    backgroundColor: '#FFFBEB',
    borderBottom: `1px solid ${colors.gray[300]}`,
    fontSize: '13px',
    color: colors.gray[700],
    textAlign: 'center',
  },
  selectAllLink: {
    background: 'none',
    border: 'none',
    color: colors.brand.blue,
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px',
    color: colors.gray[600],
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: colors.gray[600],
  },
  ordersTable: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'grid',
    gap: '16px',
    padding: '12px 16px',
    backgroundColor: colors.gray[100],
    borderRadius: '8px 8px 0 0',
    fontWeight: 600,
    fontSize: '12px',
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableHeaderCell: {
    display: 'flex',
    alignItems: 'center',
  },
  tableRow: {
    display: 'grid',
    gap: '16px',
    padding: '14px 16px',
    borderBottom: `1px solid ${colors.gray[200]}`,
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  tableCell: {
    fontSize: '14px',
    color: colors.ink.black,
  },
  checkboxCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    accentColor: colors.brand.blue,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
  },
  trackingColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  trackingCarrierRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[700],
  },
  carrierLogoInline: {
    height: '14px',
    width: 'auto',
    objectFit: 'contain',
  },
  trackingLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: colors.brand.blue,
    textDecoration: 'none',
    fontFamily: 'monospace',
  },
  trackingNumberText: {
    fontSize: '12px',
    color: colors.gray[600],
    fontFamily: 'monospace',
  },
  noTracking: {
    color: colors.gray[400],
  },
};
